import { SyntheticEvent, useEffect, useMemo, useState, MouseEvent } from 'react'
import * as R from 'remeda'
import { differenceInCalendarDays, format } from 'date-fns'
import PageHeader from '../../../components/PageHeader'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfoIcon from '@mui/icons-material/Info'
import { useAppDispatch, useAppSelector } from '../../../redux/store'
import { selectCurrentCustomerNumber } from '../../../redux/customer'
import { getFullNameOrEmail, selectCurrentUser, selectIsAdminOrOwner } from '../../../redux/user'
import {
  Checkbox,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  FormControl,
  Stack,
  Tab,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  useTheme,
  FormControlLabel,
  FormGroup,
  Menu,
  MenuItem
} from '@mui/material'
import {
  ComplianceStatus,
  CustomerTraining,
  CustomerTrainingMetadata,
  RefItem,
  TeamMember,
  Training,
  UserTraining,
  UserTrainingMetadata
} from '../../../types'
import './Training.css'
import { addError, addSuccess } from '../../../redux/alerts'
import ComplianceDonut from '../../../components/ComplianceDonut'
import ComplianceAlertBox from '../../../components/ComplianceAlertBox'
import ComplianceAlertMessage from '../../../components/ComplianceAlertMessage'
import { StyledTableCell, StyledTableRow } from '../../../components/Table.styled'
import CompliancePlip from '../../../components/CompliancePlip'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import { LightTooltip } from '../dentalenhancements/components/ToolTip'
import { AssignPayload, CourseFilterOption, TeamMemberFilterOption, TrainingSummary } from './types'
import TrainingSummaryComponent from './components/TrainingSummary'
import { downloadTrainingByYearReport, downloadTrainingReport } from './components/Reports'
import Directions from './components/Directions'
import { assignTraining, downloadCertificate, getUserTrainings as fetchUserTrainings, launchCourse, unassignTraining } from '../../../services/trainingService'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import useCustomer from '../../../hooks/useCustomer'
import useTeamMembers from '../../../hooks/useTeamMembers'
import { CertificatePayload, LaunchCoursePayload } from './types'
import { buildTrainingCompliance, enrollTraining, unenrollTraining } from '../../../services/trainingService'

const DefaultCourseFilter = {
  allCourses: { name: 'All Courses', category: 'All Courses' },
  noActiveCourses: { name: 'No Active Courses', category: 'No Courses' },
  noInactiveCourses: { name: 'No Inactive Courses', category: 'No Courses' }
}

const defaultTeamMemberFilter = { name: 'All Team Members' } as TeamMemberFilterOption

const DefaultYearFilter = 'All Years'

const UncategorizedCourse = 'Uncategorized'

interface PracticeTrainingProps {
  allCustomerTrainings: CustomerTraining[]
  customerTrainingMetadata: Partial<CustomerTrainingMetadata>
  userTrainingMetadata: UserTrainingMetadata[]
  getCustomerTrainings: () => Promise<void>
}

const PracticeTraining = (props: PracticeTrainingProps) => {
  const { allCustomerTrainings, customerTrainingMetadata, userTrainingMetadata, getCustomerTrainings } = props
  const dispatch = useAppDispatch()
  const [activeCustomerTrainings, setActiveCustomerTrainings] = useState<CustomerTraining[]>([])
  const [allUserTrainings, setAllUserTrainings] = useState<UserTraining[]>([])
  const [inactiveCustomerTrainings, setInactiveCustomerTrainings] = useState<CustomerTraining[]>([])
  const [tabIndex, setTabIndex] = useState<number>(0)
  const [activeCourseFilterOptions, setActiveCourseFilterOptions] = useState<CourseFilterOption[]>([])
  const [inactiveCourseFilterOptions, setInactiveCourseFilterOptions] = useState<CourseFilterOption[]>([])
  const [activeCourseFilter, setActiveCourseFilter] = useState<CourseFilterOption>(DefaultCourseFilter.noActiveCourses)
  const [inactiveCourseFilter, setInactiveCourseFilter] = useState<CourseFilterOption>(DefaultCourseFilter.noActiveCourses)
  const [selectedActiveTeamMember, setSelectedActiveTeamMember] = useState<TeamMemberFilterOption>(defaultTeamMemberFilter)
  const [selectedInactiveTeamMember, setSelectedInactiveTeamMember] = useState<TeamMemberFilterOption>(defaultTeamMemberFilter)
  const [yearFilterOptions, setYearFilterOptions] = useState<string[]>([])
  const [selectedYear, setSelectedYear] = useState<string>(DefaultYearFilter)
  const [loadingTrainingId, setLoadingTrainingId] = useState<number | null>(null)
  const [trainingSummary, setTrainingSummary] = useState<TrainingSummary[]>([])
  const [assignForm, setAssignForm] = useState({ show: false, assign: false })
  const [coursesToAssignOrUnassign, setCoursesToAssignOrUnassign] = useState<CustomerTraining[]>([])
  const [selectedUsersToAssignOrUnassign, setSelectedUsersToAssignOrUnassign] = useState<TeamMember[]>([])
  const [usersEligibleForAssign, setUsersEligibleForAssign] = useState<TeamMember[]>([])
  const [inactiveTabTeamMembers, setInactiveTabTeamMembers] = useState<TeamMember[]>([])
  const [displayInactiveMember, setDisplayInactiveMember] = useState(false)
  const [exportOpenEl, setExportOpenEl] = useState<HTMLElement | null>(null)
  const exportOpen = Boolean(exportOpenEl)
  const customerNumber = useAppSelector(selectCurrentCustomerNumber)
  const currentUser = useAppSelector(selectCurrentUser)
  const isAdminOrOwner = useAppSelector(selectIsAdminOrOwner)
  const isTeamMember = !isAdminOrOwner
  
  const theme = useTheme()
  const pageTabs = [`Active(${activeCustomerTrainings.length})`, `Inactive(${inactiveCustomerTrainings.length})`]
  const displayExportButton = Boolean(allCustomerTrainings.length)

  const teamMembers = useTeamMembers()

  const customer = useCustomer()

  useEffect(() => {
    if (customerNumber) {
      getUserTrainings()
    }
  }, [customerNumber])

  const userTrainings: UserTraining[] = useMemo(() => {
    if (isTeamMember) {
      const userId = Number(currentUser?.login_user_id)
      return allUserTrainings.filter((userTraining: UserTraining) => Number(userTraining?.user_id) === userId)
    }
    return allUserTrainings
  }, [isTeamMember, allUserTrainings])

  const sortedTeamMembers: TeamMember[] = useMemo(() => {
    const sorted = [...teamMembers].sort((a: TeamMember, b: TeamMember) => {
      const first = getFullNameOrEmail(a).toLowerCase()
      const second = getFullNameOrEmail(b).toLowerCase()
      return first < second ? -1 : first > second ? 1 : 0
    })

    return sorted
  }, [teamMembers])

  const activeTeamMembers: TeamMember[] = useMemo(() => {
    const activeMembers = sortedTeamMembers.filter((teamMember) => teamMember.account_active)
    setUsersEligibleForAssign(activeMembers)
    return activeMembers
  }, [sortedTeamMembers])

  const teamMembersIndex = useMemo((): Record<string, TeamMember> => {
    if (teamMembers.length === 0) {
      return {}
    }

    return R.indexBy(teamMembers, (member) => member.login_user_id)
  }, [teamMembers])

  const trainingCompliance = useMemo(() => {
    const activeCourses = allCustomerTrainings.filter((training) => training.is_active && training.training).map((training) => training.training.course_id);

    // Only show compliance for current user when access level is teamMember
    const userId = Number(currentUser?.login_user_id)

    if (isTeamMember && userTrainings.length > 0) {
      return userTrainings
        .filter(
          (training) =>
            activeCourses.some((courseId) => String(courseId) === String(training.course_id)) &&
            Number(training?.user_id) === userId &&
            training.is_active
        )
        .map((training) => {
          return {
            compliance: {
              status: training.compliance_status
            }
          }
        })
    }
    // Show compliance for all users trainings when access level is admin or owner
    else if (isAdminOrOwner && userTrainings.length > 0) {
      let compliance: { compliance: { status: number } }[] = []

      userTrainings
        .filter(
          (training) =>
            activeCourses.some((courseId) => String(courseId) === String(training.course_id)) &&
            training.is_active &&
            training?.teamMember?.account_active
        )
        .forEach((training) => {
          compliance.push({
            compliance: {
              status: training.compliance_status
            }
          })
        })
      return compliance
    }

    return []
  }, [userTrainings, activeTeamMembers, activeCustomerTrainings])

  const checkLaunchCourse = (training: UserTraining) => {
    return String(currentUser?.login_user_id) === training.user_id
  }

  const checkViewCertificate = (training: UserTraining) => {
    return training.completion_date !== null
      && (isAdminOrOwner || String(currentUser?.login_user_id) === training.user_id)
      && Boolean(training.teamMember)
  }

  const getActiveOrInactiveTrainingList = (active: boolean, trainingList: CustomerTraining[]) => {
    const trainingYears = {} as { [Identifier: number]: number }
    const teamMembersWithTrainings: { [Identifier: string]: TeamMember } = {}
    const trainingIndex = R.indexBy(
      R.map(trainingList, (customerTraining) => {
        const expirationDate = new Date(customerTraining.expiration_date)
        const formattedExpirationDate = format(expirationDate, 'P')
        const expiresIn = differenceInCalendarDays(expirationDate, new Date())
        const displayName = `${customerTraining.training.name} (${customerTraining.training.year})`
        if (!active && !trainingYears[customerTraining.training.year]) trainingYears[customerTraining.training.year] = 1
        const refItem = customerTraining.training.ref_item ?? ({ subcategory: UncategorizedCourse } as RefItem)
        if (!refItem.subcategory) refItem.subcategory = UncategorizedCourse
        customerTraining.training.ref_item = refItem
        return { ...customerTraining.training, expiresIn, formattedExpirationDate, displayName, is_active: customerTraining.is_active }
      }),
      R.prop('course_id')
    )

    const memberTrainingSummary: { [Identifier: string]: TrainingSummary } = {}
    if (active) {
      activeTeamMembers.forEach((member) => {
        memberTrainingSummary[member.login_user_id] = {
          userId: member.login_user_id,
          userName: getFullNameOrEmail(member),
          overdue: 0,
          notStarted: 0,
          inProgress: 0,
          completed: 0,
          complianceStatus: 4
        }
      })
    }

    userTrainings.forEach((training) => {
      training.teamMember = teamMembersIndex[training.user_id]

      if (trainingIndex[training.course_id]) {
        if (training.teamMember?.account_active || (training?.teamMember && displayInactiveMember)) {
          if (isAdminOrOwner || (isTeamMember && Number(training.user_id) === Number(currentUser?.login_user_id))) {
            if (trainingIndex[training.course_id]?.userTrainings) {
              trainingIndex[training.course_id].userTrainings?.push(training)
            } else {
              trainingIndex[training.course_id].userTrainings = [training]
            }
          }
        }

        if (training.teamMember?.account_active && training.is_active && active) {
          let memberSummary = memberTrainingSummary[training.teamMember?.login_user_id]
          const trainingMetadata = userTrainingMetadata.find((metadata) => metadata.user_id == training.teamMember?.login_user_id)
          memberSummary = {
            ...memberSummary,
            overdue: trainingMetadata?.past_due_count ?? 0,
            notStarted: memberSummary.notStarted ?? 0,
            completed: trainingMetadata?.completed_count ?? 0,
            complianceStatus: trainingMetadata?.compliance.compliance_status ?? 4
          }

          if (training.compliance_status === 2) {
            if (training.enrollment_date === null) {
              memberSummary.notStarted++
            } else {
              memberSummary.inProgress++
            }
          }
          memberTrainingSummary[training.teamMember?.login_user_id] = memberSummary
        }
      }

      if (
        !active &&
        trainingIndex[training.course_id] &&
        !trainingIndex[training.course_id].is_active &&
        !teamMembersWithTrainings[training.user_id]
      ) {
        if (training.teamMember?.account_active || (training?.teamMember && displayInactiveMember)) {
          teamMembersWithTrainings[training.user_id] = training.teamMember
        }
      }
    })

    const summary = Object.keys(memberTrainingSummary).map((userId) => memberTrainingSummary[userId])

    const filterOptions = R.pipe(
      trainingList,
      R.map((t) => ({ category: t.training.ref_item?.subcategory ?? UncategorizedCourse, name: t.training.name, year: t.training.year })),
      R.sort((a, b) => {
        if (a.category === b.category) {
          return a.name > b.name ? 1 : -1
        }
        return a?.category > b?.category ? 1 : -1
      })
    )

    return {
      filterOptions,
      summary,
      trainingIndex,
      trainingYears,
      teamMembersWithTrainings,
      trainingList: R.pipe(
        trainingIndex,
        R.values,
        R.groupBy((training) => training?.ref_item?.subcategory),
        R.toPairs,
        R.map(([category, trainingList]) => ({ category, trainingList })),
        R.sortBy(R.prop('category'))
      )
    }
  }

  const activeCustomerTrainingList = useMemo(() => {
    const activeTrainings = R.filter(allCustomerTrainings, (customerTraining: CustomerTraining) => {
      if (isTeamMember)
        return (
          customerTraining.is_active &&
          userTrainings.find((ut) => ut.is_active && Number(ut.course_id) === Number(customerTraining.training.course_id)) !== undefined
        )
      return customerTraining.is_active
    })

    setActiveCustomerTrainings(activeTrainings)
    const activeCustomerTrainingList = structuredClone(activeTrainings)
    if (activeCustomerTrainingList.length > 0) {
      const activeTrainings = getActiveOrInactiveTrainingList(true, activeCustomerTrainingList)
      const filterOptions = activeTrainings?.filterOptions ?? []
      const summary: TrainingSummary[] = activeTrainings?.summary ?? []
      const trainingList = activeTrainings?.trainingList ?? []

      setTrainingSummary(summary)

      const defaultCourse = filterOptions.length > 0 ? DefaultCourseFilter.allCourses : DefaultCourseFilter.noActiveCourses
      setActiveCourseFilter(defaultCourse)
      setActiveCourseFilterOptions(filterOptions)

      return trainingList
    }

    return []
  }, [allCustomerTrainings, userTrainings, activeTeamMembers, displayInactiveMember])

  const inactiveCustomerTrainingList = useMemo(() => {
    const inactiveTrainings = R.filter(allCustomerTrainings, (customerTraining: CustomerTraining) => {
      if (isTeamMember)
        return (
          !customerTraining.is_active &&
          userTrainings.find((ut) => Number(ut.course_id) === Number(customerTraining.training.course_id)) !== undefined
        )
      return !customerTraining.is_active
    })
    setInactiveCustomerTrainings(inactiveTrainings)
    const inactiveCustomerTrainingList = structuredClone(inactiveTrainings)
    if (inactiveCustomerTrainingList.length > 0) {
      const inactiveTrainings = getActiveOrInactiveTrainingList(false, inactiveCustomerTrainingList)
      const filterOptions = inactiveTrainings?.filterOptions ?? []
      const trainingList = inactiveTrainings?.trainingList ?? []
      const trainingYears = inactiveTrainings?.trainingYears ?? {}
      const teamMembersWithTrainings = inactiveTrainings?.teamMembersWithTrainings ?? {}

      const defaultCourse = filterOptions.length > 0 ? DefaultCourseFilter.allCourses : DefaultCourseFilter.noInactiveCourses
      setInactiveCourseFilter(defaultCourse)
      setInactiveCourseFilterOptions(R.uniqBy(filterOptions, R.prop('name')))
      setYearFilterOptions(Object.keys(trainingYears))
      setInactiveTabTeamMembers(
        R.values(teamMembersWithTrainings).sort((a, b) => (getFullNameOrEmail(a).toLowerCase() < getFullNameOrEmail(b).toLowerCase() ? -1 : 1))
      )

      return trainingList
    }

    return []
  }, [allCustomerTrainings, userTrainings, teamMembers, displayInactiveMember])

  const overallTrainingCompliance = useMemo(() => {
    // Only show overall compliance for current user when access level is teamMember
    if (isTeamMember && userTrainingMetadata.length > 0) {
      const userMetadata = userTrainingMetadata.find((user) => Number(user.user_id) === Number(currentUser?.login_user_id))
      if (userMetadata) {
        return {
          compliance_status: userMetadata?.compliance?.compliance_status ?? ComplianceStatus.Unassigned,
          message: userMetadata?.compliance?.message_long ?? ''
        }
      }
      return {
        compliance_status: ComplianceStatus.Unassigned,
        message: ''
      }
    }
    // Show overall compliance for practice when access level is owner or admin
    else if (isAdminOrOwner && Object.keys(customerTrainingMetadata).length > 0) {
      return {
        compliance_status: customerTrainingMetadata?.training_compliance_status ?? ComplianceStatus.Unassigned,
        message: customerTrainingMetadata?.compliance?.message_long ?? ''
      }
    }

    return {
      compliance_status: ComplianceStatus.Unassigned,
      message: ''
    }
  }, [customerTrainingMetadata, userTrainingMetadata, userTrainings])

  const getUserTrainings = async () => {
    try {
      const data = await fetchUserTrainings(customerNumber)
      return setAllUserTrainings(data)
    } catch (error) {
      console.error(error)
      dispatch(addError('There was an issue retrieving the user trainings'))
    }
  }

  const handleDownloadReport = () => {
    const customerTrainings = tabIndex === 0 ? activeCustomerTrainings : inactiveCustomerTrainings

    downloadTrainingReport(
      customerTrainings,
      customer,
      displayInactiveMember,
      tabIndex,
      teamMembers,
      teamMembersIndex,
      userTrainings,
      yearFilterOptions
    )
  }

  const handleDownloadTrainingByYearReport = () => {
    if (!isAdminOrOwner) {
      return
    }

    downloadTrainingByYearReport(
      allCustomerTrainings,
      customer,
      teamMembers,
      teamMembersIndex,
      userTrainings
    )
  }

  const handleExportClick = (event: MouseEvent<HTMLButtonElement>) => {
    setExportOpenEl(event.currentTarget)
  }

  const handleExportClose = () => {
    setExportOpenEl(null)
  }

  const handleExportReport = () => {
    handleExportClose()
    handleDownloadReport()
  }

  const handleExportTrainingByYear = () => {
    handleExportClose()
    handleDownloadTrainingByYearReport()
  }

  const handleLaunchCourse = async (courseId: string, teamMember: TeamMember) => {
    try {
      const { customer_number, login_user_id, email, firstname, lastname } = teamMember

      const payload: LaunchCoursePayload = {
        courseId,
        user: {
          customer_number,
          login_user_id,
          email,
          firstname,
          lastname,
        },
      }

      const url = await launchCourse(customerNumber, payload)

      window.open(url, '_blank')
    } catch (err: any) {
      console.error(err)
      const errorMessage = err?.response?.data?.message ?? 'There was an error launching your course, please try again later'

      dispatch(addError(errorMessage))
    }
  }

  const handleDownloadCertificate = async (userTraining: UserTraining) => {
    try {
      if (!userTraining.teamMember) return

      const { lms_user_id, teamMember } = userTraining
      const { customer_number } = teamMember

      const payload: CertificatePayload = {
        courseId: userTraining.course_id,
        user: { customer_number, lms_user_id }
      }

      setLoadingTrainingId(userTraining.id)

      const certificate = await downloadCertificate(customerNumber, payload)

      setLoadingTrainingId(null)

      if (certificate) {
        const { pdf } = certificate
        window.open(pdf, '_blank')
      }
    } catch (error: any) {
      setLoadingTrainingId(null)

      console.error(error)
      const errorMessage = error?.response?.data?.message ?? 'There was an error downloading your certificate, please try again later'

      dispatch(addError(errorMessage))
    }
  }

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabIndex(newValue)
    handleExportClose()
  }

  const getTrainingStatus = (training: UserTraining) => {
    switch (training.compliance_status) {
      case 1:
        return 'Completed'
      case 2:
        return training.enrollment_date ? 'In Progress' : 'Not Started'
      case 3:
        return 'Over Due'
      default:
        return tabIndex === 1 && training.completion_date ? 'Completed' : 'Inactive'
    }
  }

  const filteredUserTrainings = (training: Training, selectedMember: TeamMemberFilterOption) => {
    const trainings = training?.userTrainings && training?.userTrainings?.length > 0 ? training.userTrainings : []
    if (selectedMember.name === defaultTeamMemberFilter.name) return trainings
    return trainings.filter((userTraining) => {
      return userTraining.user_id === selectedMember.login_user_id
    })
  }

  const assignOrUnassign = async () => {
    setAssignForm({ ...assignForm, show: false })

    const payload: AssignPayload[] = selectedUsersToAssignOrUnassign.map(({ customer_number, login_user_id, email, firstname, lastname }) => {
      return {
        customer_number,
        login_user_id,
        email,
        firstname,
        lastname,
        training: coursesToAssignOrUnassign.map(({ training: { course_id, name, ce_credits, days_to_complete } }) => ({
          course_id,
          name,
          ce_credits,
          days_to_complete
        }))
      }
    })

    const services = {
      assignment: assignForm.assign ? assignTraining : unassignTraining,
      enrollment: assignForm.assign ? enrollTraining : unenrollTraining
    }

    try {
      const data = await services.assignment(customerNumber, payload)
      await services.enrollment(customerNumber, data.user_training_ids)

      await buildTrainingCompliance(
        customerNumber,
        assignForm.assign ? 'assign' : 'unassign'
      )

      dispatch(addSuccess(data.message))

      getCustomerTrainings()
      getUserTrainings()
    } catch (error: any) {
      const action = assignForm.assign ? 'assigning' : 'unassigning'
      const fallback = `There was an error ${action} training`

      dispatch(addError(error?.response?.data?.message ?? fallback))
    } finally {
      setCoursesToAssignOrUnassign([])
      setSelectedUsersToAssignOrUnassign([])
    }
  }

  const handleCloseAssignForm = () => {
    setAssignForm({ show: false, assign: false })
    setCoursesToAssignOrUnassign([])
    setSelectedUsersToAssignOrUnassign([])
    setUsersEligibleForAssign(activeTeamMembers)
  }

  const handleChangeCourseToAssign = (selectedTrainings: CustomerTraining[]) => {
    setCoursesToAssignOrUnassign(selectedTrainings)
    if (selectedTrainings.length > 1 || selectedTrainings.length === 0) {
      setUsersEligibleForAssign(activeTeamMembers)
    } else {
      const eligibleUsers = activeTeamMembers.filter((member) => {
        const trainingAssigned =
          R.intersectionWith(
            userTrainings,
            selectedTrainings,
            (ut, t) => Number(t.training.course_id) === Number(ut.course_id) && Number(ut.teamMember?.login_user_id) === Number(member.login_user_id)
          ).length > 0
        return assignForm.assign ? !trainingAssigned : trainingAssigned
      })
      setUsersEligibleForAssign(eligibleUsers)
      setSelectedUsersToAssignOrUnassign(
        R.intersectionWith(selectedUsersToAssignOrUnassign, eligibleUsers, (su, eu) => Number(su.login_user_id) === Number(eu.login_user_id))
      )
    }
  }

  const handleInactiveUserToggle = () => {
    setDisplayInactiveMember(!displayInactiveMember)
  }

  const TabContent = () => {
    const trainingList = tabIndex === 0 ? activeCustomerTrainingList : inactiveCustomerTrainingList
    const courseFilterOptions = tabIndex === 0 ? activeCourseFilterOptions : inactiveCourseFilterOptions
    const courseFilter = tabIndex === 0 ? activeCourseFilter : inactiveCourseFilter
    const setCourseFilter = tabIndex === 0 ? setActiveCourseFilter : setInactiveCourseFilter
    const teamMemberFilterOptions: TeamMemberFilterOption[] = (
      tabIndex === 0 ? (displayInactiveMember ? sortedTeamMembers : activeTeamMembers) : inactiveTabTeamMembers
    ).map((member) => ({ ...member, name: getFullNameOrEmail(member) }))
    const selectedTeamMember = tabIndex === 0 ? selectedActiveTeamMember : selectedInactiveTeamMember
    const setSelectedTeamMember = tabIndex === 0 ? setSelectedActiveTeamMember : setSelectedInactiveTeamMember
    const hasInactiveMembers = sortedTeamMembers.findIndex((member) => !member.account_active) >= 0

    return (
      <>
        <Box className="flex flex-end" style={{ marginBottom: 16 }}>
          {hasInactiveMembers && (
            <>
              <FormGroup sx={{ alignSelf: 'center' }}>
                <FormControlLabel
                  label="Inactive Members"
                  control={<Checkbox size="small" checked={displayInactiveMember} onChange={handleInactiveUserToggle} />}
                  sx={{ marginRight: '8px', '& .MuiTypography-root': { fontSize: 13 } }}
                />
              </FormGroup>
              <Box sx={{ display: 'flex', alignItems: 'center', alignSelf: 'center' }}>
                <LightTooltip title="Check the box to view trainings for Inactive members">
                  <InfoIcon fontSize="small" />
                </LightTooltip>
              </Box>
            </>
          )}
        </Box>
        <Box className="filter-container flex flex-space-between" style={{ padding: 0 }}>
          <Box className="flex">
            <Autocomplete
              disabled={trainingList.length === 0}
              clearOnEscape
              disableClearable={courseFilter.name === DefaultCourseFilter.allCourses.name}
              onChange={(event, newValue) => {
                if (newValue != null) {
                  setCourseFilter(newValue)
                } else {
                  setCourseFilter(DefaultCourseFilter.allCourses)
                }
              }}
              isOptionEqualToValue={(option, value) => option.category === option.name || option.name === value.name}
              options={courseFilterOptions}
              groupBy={(option) => option?.category}
              getOptionLabel={(option) => `${option.name} ${option.year ?? ''}`}
              renderInput={(params) => <TextField {...params} />}
              value={courseFilter}
              renderGroup={(params) => (
                <div key={params.key}>
                  {Number(params.key) == 0 && (
                    <li>
                      <div
                        className={`filter-option-group${courseFilter.name === DefaultCourseFilter.allCourses.name ? ' selected' : ''}`}
                        onClick={() => {
                          setCourseFilter(DefaultCourseFilter.allCourses)
                        }}
                      >
                        {DefaultCourseFilter.allCourses.name}
                      </div>
                    </li>
                  )}
                  <li>
                    <div
                      className={`filter-option-group${courseFilter.name === params.group ? ' selected' : ''}`}
                      onClick={() => {
                        setCourseFilter({ name: params.group, category: params.group })
                      }}
                    >
                      {params.group}
                    </div>
                    <ul className="filter-options-list" style={{ padding: 0 }}>
                      {params.children}
                    </ul>
                  </li>
                </div>
              )}
              style={{ width: '200px' }}
              size="small"
              className="autocomplete-filter"
            />
            {isAdminOrOwner && (
              <Autocomplete
                onChange={(event, newValue) => {
                  if (newValue) {
                    setSelectedTeamMember(newValue)
                  } else {
                    setSelectedTeamMember(defaultTeamMemberFilter)
                  }
                }}
                disableClearable={selectedTeamMember.name === defaultTeamMemberFilter.name}
                options={[
                  defaultTeamMemberFilter,
                  ...teamMemberFilterOptions.map((teamMember) => ({ ...teamMember, name: getFullNameOrEmail(teamMember) }))
                ]}
                getOptionLabel={(option) => option.name}
                renderOption={(props, option) => {
                  const classes = `filter-option ${selectedTeamMember?.name === option.name ? 'selected' : ''}`.trim()
                  const value = `${option.name} ${option.account_active || option.name === defaultTeamMemberFilter.name ? '' : ' (Inactive)'}`.trim()
                  return (
                    <li {...props} className={classes}>
                      {value}
                    </li>
                  )
                }}
                renderInput={(params) => <TextField {...params} />}
                value={selectedTeamMember}
                size="small"
                className="autocomplete-filter"
                style={{ width: '200px' }}
              />
            )}
            {tabIndex === 1 && (
              <Autocomplete
                disableClearable={selectedYear === DefaultYearFilter}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setSelectedYear(newValue)
                  } else {
                    setSelectedYear(DefaultYearFilter)
                  }
                }}
                options={[DefaultYearFilter, ...yearFilterOptions]}
                renderOption={(props, option) => (
                  <li {...props} className={`filter-option${selectedYear === option ? ' selected' : ''}`}>
                    {option}
                  </li>
                )}
                renderInput={(params) => <TextField {...params} />}
                value={selectedYear}
                size="small"
                className="autocomplete-filter"
              />
            )}
          </Box>
          {tabIndex === 0 && isAdminOrOwner && (
            <Box className="assign-button-container">
              <Button
                startIcon={<AddIcon />}
                disabled={assignForm.show}
                variant="contained"
                style={{ marginRight: 10 }}
                onClick={() => setAssignForm({ show: true, assign: true })}
              >
                Assign Training
              </Button>
              <Button
                startIcon={<RemoveIcon />}
                disabled={assignForm.show}
                variant="contained"
                onClick={() => setAssignForm({ show: true, assign: false })}
              >
                Unassign Training
              </Button>
            </Box>
          )}
        </Box>
        {assignForm.show && tabIndex === 0 && isAdminOrOwner && (
          <Box style={{ border: '1px solid grey', padding: 10, marginTop: 10, marginBottom: 10 }}>
            {assignForm.assign ? (
              <Typography style={{ fontSize: '13px' }} className="assign-form-text">
                This will make the training required for your practice. Using this will enable a compliance status that will help keep your team
                members on track. Team members can only take trainings that have been assigned to them.
              </Typography>
            ) : (
              <Typography style={{ fontSize: '13px' }} className="assign-form-text">
                Unassign Training allows you to remove a required course from one of your team members. Please Select the course and team member.
              </Typography>
            )}
            <form style={{ marginTop: 10, marginBottom: 10 }}>
              <Box className="flex">
                <FormControl>
                  <Autocomplete
                    multiple
                    options={activeCustomerTrainings.sort((a, b) =>
                      `${a.training.name} ${a.training.year}`.toLowerCase() < `${b.training.name} ${b.training.year}`.toLowerCase() ? -1 : 1
                    )}
                    value={coursesToAssignOrUnassign}
                    getOptionLabel={(option) => `${option.training.name} ${option.training.year}`}
                    onChange={(_, value) => {
                      handleChangeCourseToAssign(value as CustomerTraining[])
                    }}
                    renderInput={(params) => <TextField {...params} label={'Course *'} placeholder="Select Course(s)" />}
                    sx={{ width: '500px' }}
                    size="small"
                    className="autocomplete-filter"
                  />
                </FormControl>
                <Button
                  disabled={coursesToAssignOrUnassign.length === activeCustomerTrainings.length}
                  size="small"
                  style={{ marginLeft: '10px', height: '37px', marginTop: 'auto', fontSize: '13px' }}
                  variant="outlined"
                  onClick={() => setCoursesToAssignOrUnassign(activeCustomerTrainings)}
                >
                  {assignForm.assign ? 'Assign' : 'Unassign'} All Courses
                </Button>
              </Box>
              <Box className="flex" sx={{ marginTop: '10px' }}>
                <FormControl>
                  <Autocomplete
                    multiple
                    options={usersEligibleForAssign}
                    value={selectedUsersToAssignOrUnassign}
                    getOptionLabel={(option) => getFullNameOrEmail(option)}
                    onChange={(event, value) => setSelectedUsersToAssignOrUnassign(value)}
                    renderInput={(params) => (
                      <TextField {...params} label={assignForm.assign ? 'Assign To *' : 'Unassign From *'} placeholder="Select Member(s)" />
                    )}
                    sx={{ width: '500px' }}
                    size="small"
                    className="autocomplete-filter"
                  />
                </FormControl>
                <Button
                  disabled={selectedUsersToAssignOrUnassign.length === activeTeamMembers.length}
                  size="small"
                  style={{ marginLeft: '10px', height: '37px', marginTop: 'auto', fontSize: '13px' }}
                  variant="outlined"
                  onClick={() => setSelectedUsersToAssignOrUnassign(usersEligibleForAssign)}
                >
                  {assignForm.assign ? 'Assign to' : 'Unassign from'} All
                </Button>
              </Box>
              <Box className="flex flex-end">
                <Button
                  disabled={selectedUsersToAssignOrUnassign.length === 0 || coursesToAssignOrUnassign.length === 0}
                  variant="contained"
                  onClick={assignOrUnassign}
                >
                  Save
                </Button>
                <Button onClick={handleCloseAssignForm}>Cancel</Button>
              </Box>
            </form>
          </Box>
        )}
        {tabIndex === 0 && <Directions />}
        {tabIndex === 0 && isAdminOrOwner && trainingList.length > 0 && (
          <TrainingSummaryComponent
            defaultTeamMemberFilter={defaultTeamMemberFilter}
            selectedTeamMember={selectedTeamMember}
            trainingSummary={trainingSummary}
          />
        )}
        <Box className="course-container">
          <div className="training-header">Training Courses</div>
          <div className="p-16">
            {trainingList.length > 0 ? (
              trainingList
                .filter(({ category, trainingList }) => {
                  return trainingList.some((training) => {
                    return courseFilter.category === DefaultCourseFilter.allCourses.name || category === courseFilter.category
                  })
                })
                .map(({ category, trainingList }) => (
                  <div key={category}>
                    <Accordion disableGutters sx={{ mb: 1 }} defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <div className="category-header">{category}</div>
                      </AccordionSummary>
                      <AccordionDetails className="category-body" sx={{ paddingTop: '16px' }}>
                        {trainingList
                          .filter(
                            (training) =>
                              (tabIndex === 0 || selectedYear === DefaultYearFilter || Number(selectedYear) === training.year) &&
                              (courseFilter.category === DefaultCourseFilter.allCourses.name ||
                                courseFilter.name === training.name ||
                                activeCourseFilter.name === category)
                          )
                          .map((training) => (
                            <Accordion disableGutters key={training.id} sx={{ mb: 1 }} defaultExpanded>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box className="flex">
                                  <Typography>{training.displayName}</Typography>
                                </Box>
                                {tabIndex === 0 && training.expiresIn <= 14 && (
                                  <Box className="flex flex-end">
                                    <Typography fontSize="13px" color="red" style={{ position: 'absolute', right: 50 }}>
                                      {training.expiresIn} day(s) remaining. course will expire on {training.formattedExpirationDate}
                                    </Typography>
                                  </Box>
                                )}
                              </AccordionSummary>
                              <AccordionDetails sx={{ padding: 0 }}>
                                {filteredUserTrainings(training, selectedTeamMember).length > 0 ? (
                                  <Table>
                                    <TableHead>
                                      <TableRow>
                                        <StyledTableCell></StyledTableCell>
                                        <StyledTableCell>User</StyledTableCell>
                                        <StyledTableCell>Active/Inactive</StyledTableCell>
                                        <StyledTableCell>Status</StyledTableCell>
                                        <StyledTableCell>CE Credits</StyledTableCell>
                                        <StyledTableCell>Due Date</StyledTableCell>
                                        <StyledTableCell>Completion Date</StyledTableCell>
                                        <StyledTableCell>Certificate</StyledTableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {filteredUserTrainings(training, selectedTeamMember)
                                        .sort((a, b) => {
                                          let nameA = getFullNameOrEmail(a?.teamMember).toLowerCase()
                                          let nameB = getFullNameOrEmail(b?.teamMember).toLowerCase()
                                          if (nameA < nameB) {
                                            return -1
                                          }
                                          if (nameA > nameB) {
                                            return 1
                                          }
                                          return 0
                                        })
                                        .map((userTraining) => {
                                          const trainingStatus = getTrainingStatus(userTraining)
                                          const isActiveMember = userTraining?.teamMember?.account_active
                                          const complianceStatus = isActiveMember ? userTraining.compliance_status : ComplianceStatus.Inactive

                                          return (
                                            <StyledTableRow key={userTraining.id}>
                                              <StyledTableCell>
                                                <CompliancePlip className="m-r-8" status={complianceStatus} />
                                              </StyledTableCell>
                                              <StyledTableCell>{getFullNameOrEmail(userTraining?.teamMember)}</StyledTableCell>
                                              <StyledTableCell>{isActiveMember ? 'Active' : 'Inactive'}</StyledTableCell>
                                              <StyledTableCell>
                                                {trainingStatus !== 'Completed' && tabIndex === 1 ? 'Incomplete' : trainingStatus}
                                              </StyledTableCell>
                                              <StyledTableCell>{training.ce_credits}</StyledTableCell>
                                              <StyledTableCell>
                                                {userTraining?.due_date ? format(new Date(userTraining.due_date), 'P') : ''}
                                              </StyledTableCell>
                                              <StyledTableCell>
                                                {userTraining?.completion_date ? format(new Date(userTraining.completion_date), 'P') : ''}
                                              </StyledTableCell>
                                              <StyledTableCell>
                                                {checkViewCertificate(userTraining) && (
                                                  <Button
                                                    onClick={() => handleDownloadCertificate(userTraining)}
                                                    variant="contained"
                                                    size="small"
                                                    sx={{ mr: 1 }}
                                                    disabled={loadingTrainingId === userTraining.id}
                                                  >
                                                    {loadingTrainingId === userTraining.id ? 'Loading' : 'View'}
                                                  </Button>
                                                )}
                                                {tabIndex === 0 && checkLaunchCourse(userTraining) && (
                                                  <Button
                                                    size="small"
                                                    variant="contained"
                                                    disabled={Boolean(userTraining.enrollment_date) === false}
                                                    onClick={() => userTraining.teamMember && handleLaunchCourse(userTraining.course_id, userTraining.teamMember)}
                                                  >
                                                    { 
                                                      Boolean(userTraining.enrollment_date) === false
                                                        && 'Pending'
                                                    }
                                                    {
                                                      Boolean(userTraining.enrollment_date)
                                                        && Boolean(userTraining.completion_date) === false
                                                        && 'Launch Course'
                                                    }
                                                    {
                                                      Boolean(userTraining.enrollment_date)
                                                        && Boolean(userTraining.completion_date)
                                                        && 'Replay'
                                                    }
                                                  </Button>
                                                )}
                                              </StyledTableCell>
                                            </StyledTableRow>
                                          )
                                        })}
                                    </TableBody>
                                  </Table>
                                ) : (
                                  <Typography style={{ padding: 15, borderTop: '1px solid rgba(0, 0, 0, 0.12)', fontSize: '13px' }}>
                                    This training has not been assigned to any team members.{' '}
                                    {tabIndex === 0 ? 'Please use the "Assign Training" button to assign training' : ''}
                                  </Typography>
                                )}
                              </AccordionDetails>
                            </Accordion>
                          ))}
                      </AccordionDetails>
                    </Accordion>
                  </div>
                ))
            ) : (
              <Typography>{`There are no ${tabIndex === 0 ? 'active' : 'inactive'} trainings for this practice.`}</Typography>
            )}
          </div>
        </Box>
      </>
    )
  }

  return (
    <>
      <PageHeader iconName={'graduation-cap'} headerName={'Training'} />
      <Box sx={{ width: '100%', color: theme.palette.color50555b.main }} className="training-container">
        <Box sx={{ width: '25%' }} className="compliance-column">
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }} className="status-header">
            Status
          </Box>
          <Stack className={'m-t-16'} spacing={1.5}>
            <ComplianceAlertBox
              compliance_status={overallTrainingCompliance.compliance_status}
              data-testid="compliance-alert-box"
            ></ComplianceAlertBox>
            <ComplianceAlertMessage
              compliance_status={overallTrainingCompliance.compliance_status}
              message={overallTrainingCompliance.message}
              data-testid="compliance-alert-message"
            ></ComplianceAlertMessage>
            <ComplianceDonut image="chart-training" label="Training" values={trainingCompliance} inactiveLabel={'Unassigned'} />
          </Stack>
        </Box>
        <Box sx={{ width: '75%' }}>
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Tabs value={tabIndex} onChange={handleTabChange}>
                {pageTabs.length && pageTabs.map((tab, index) => <Tab key={index} label={tab} />)}
              </Tabs>
            </Box>
            {displayExportButton && (
              <Box className="filter-container flex flex-space-between" sx={{ padding: 0, mt: 1.5, mb: 1.5 }}>
                <Box />
                <Box className="assign-button-container">
                  <div className="dropdown">
                    <Button
                      aria-haspopup="true"
                      variant="contained"
                      size="small"
                      name="export"
                      onClick={handleExportClick}
                      sx={{ fontSize: '13px', padding: '6px 12px' }}
                    >
                      Export
                      <FontAwesomeIcon icon="caret-down" size="sm" className="m-l-8" />
                    </Button>
                    <Menu
                      id="account-menu"
                      anchorEl={exportOpenEl}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right'
                      }}
                      open={exportOpen}
                      onClose={handleExportClose}
                      MenuListProps={{
                        'aria-labelledby': 'export-button'
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                      }}
                    >
                      <MenuItem onClick={handleExportReport}>
                        {tabIndex === 0 ? 'Active Export' : 'Inactive Export'}
                      </MenuItem>
                      {isAdminOrOwner && (
                        <MenuItem onClick={handleExportTrainingByYear}>Training By Year</MenuItem>
                      )}
                    </Menu>
                  </div>
                </Box>
              </Box>
            )}
          </Box>
          <TabContent />
        </Box>
      </Box>
    </>
  )
}

export default PracticeTraining
